export interface BagGroupMemberEditSnapshot {
  memberUids: string[];
  memberSummaries: Array<{
    uid: string;
    displayName: string;
    dancerNames: string[];
    dancers?: Array<{ id: string; name: string }>;
    addedDirectly?: boolean;
    isLead?: boolean;
    noAccount?: boolean;
  }>;
}

export interface BagGroupDetailEditSnapshot {
  name: string;
  seasonId: string;
  seasonName: string;
}

export class BagGroupEditConflictError extends Error {
  constructor() {
    super('Someone else updated this Bag Group. Your changes were not saved. Review the latest version and try again.');
    this.name = 'BagGroupEditConflictError';
  }
}

function strings(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? [...value].sort()
    : null;
}

function memberVersion(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const normalized: Array<{ uid: string; [key: string]: unknown }> = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') return null;
    const member = item as Record<string, unknown>;
    const dancerNames = strings(member.dancerNames);
    if (typeof member.uid !== 'string' || typeof member.displayName !== 'string' || !dancerNames) {
      return null;
    }
    // Absent legacy flags and explicit false describe the same membership.
    // Unknown flag types or malformed dancer identities are not safe snapshots.
    const flags: Record<string, boolean> = {};
    for (const flag of ['addedDirectly', 'isLead', 'noAccount']) {
      const raw = member[flag];
      if (raw != null && typeof raw !== 'boolean') return null;
      flags[flag] = raw === true;
    }
    const dancers: Array<{ id: string; name: string }> = [];
    if (member.dancers != null) {
      if (!Array.isArray(member.dancers)) return null;
      for (const raw of member.dancers) {
        if (!raw || typeof raw !== 'object' || typeof raw.id !== 'string' || typeof raw.name !== 'string') return null;
        dancers.push({ id: raw.id, name: raw.name });
      }
    }
    dancers.sort((left, right) => left.id.localeCompare(right.id) || left.name.localeCompare(right.name));
    normalized.push({
      uid: member.uid,
      displayName: member.displayName,
      dancerNames,
      dancers,
      ...flags,
    });
  }
  normalized.sort((left, right) => left.uid.localeCompare(right.uid));
  return JSON.stringify(normalized);
}

export function bagGroupMembersStillMatch(
  current: Record<string, unknown>,
  expected: BagGroupMemberEditSnapshot
): boolean {
  const currentUids = strings(current.memberUids);
  const expectedUids = strings(expected.memberUids);
  const currentMembers = memberVersion(current.memberSummaries);
  const expectedMembers = memberVersion(expected.memberSummaries);
  return currentUids !== null
    && expectedUids !== null
    && currentMembers !== null
    && expectedMembers !== null
    && JSON.stringify(currentUids) === JSON.stringify(expectedUids)
    && currentMembers === expectedMembers;
}

export function bagGroupDetailsStillMatch(
  current: Record<string, unknown>,
  expected: BagGroupDetailEditSnapshot
): boolean {
  return current.name === expected.name
    && current.seasonId === expected.seasonId
    && current.seasonName === expected.seasonName;
}

/**
 * A group's name and season, trimmed and checked, or the plain reason they
 * cannot be saved. Pure, so the edit panel can run every check BEFORE it
 * saves anything: a bad name used to be found only after the leads were
 * already saved and a new lead already told, which left half a save behind
 * an error (refute round 1 of plan BG-6). updateBagGroupDetails runs the
 * same checks, so the two can never disagree.
 */
export function cleanBagGroupDetailsInput(input: BagGroupDetailEditSnapshot): BagGroupDetailEditSnapshot {
  const name = input.name.trim();
  const seasonId = input.seasonId.trim();
  const seasonName = input.seasonName.trim();
  if (!name) throw new Error('Bag Group name is required.');
  if (name.length > 120) throw new Error('Bag Group names can be up to 120 characters.');
  if (!seasonId || !seasonName) throw new Error('Choose a season first.');
  return { name, seasonId, seasonName };
}

/**
 * A bag item's editable fields exactly as STORED: each trimmed, null when
 * missing, empty or not a string, with none of the defaults the page shows
 * (bagGroups.ts normalizeItem keeps them as BagGroupItemDoc.stored).
 */
export type BagGroupItemStoredFields = Record<
  'kind' | 'title' | 'note' | 'competitionName' | 'competitionDate'
  | 'countdownEventId' | 'dropOffDate' | 'quantity' | 'dropOffLocation',
  string | null
>;

/** What an item edit sends as expectedFields: field name to stored value, or null. */
export type BagGroupItemExpectedFields = Partial<Record<keyof BagGroupItemStoredFields, string | null>>;

/**
 * What an item edit sends as expectedFields (BG-6 final review follow-up 3):
 * for each field it changes, the value STORED when Edit item opened, or null
 * for nothing, never a shown default (the "Item" a missing title shows, the
 * dancer-count quantity, a formatted date). The item lane
 * (netlify/functions/lib/bagGroupItemLane.mjs comparableItemValue) compares
 * these trimmed, with null, missing and non-strings all as nothing, which is
 * exactly how the stored values are kept. Undefined when nothing changed or
 * the stored values are unknown, so the edit goes in the older shape the
 * lane still accepts.
 */
export function bagGroupItemExpectedFields(
  stored: BagGroupItemStoredFields | undefined,
  changed: Partial<Record<keyof BagGroupItemStoredFields, unknown>>,
): BagGroupItemExpectedFields | undefined {
  const keys = (Object.keys(changed) as Array<keyof BagGroupItemStoredFields>)
    .filter((key) => Object.prototype.hasOwnProperty.call(stored ?? {}, key));
  if (!stored || keys.length === 0 || keys.length !== Object.keys(changed).length) return undefined;
  // An older stored value longer than the lane takes as an expected value
  // (EXPECTED_VALUE_MAX in bagGroupItemLane.mjs) is left out of the guard,
  // so that field still saves, unguarded, the way it did before the guard.
  const guarded = keys.filter((key) => (stored[key] ?? '').length <= BAG_ITEM_EXPECTED_VALUE_MAX);
  if (guarded.length === 0) return undefined;
  return Object.fromEntries(guarded.map((key) => [key, stored[key] ?? null])) as BagGroupItemExpectedFields;
}

/** The longest expected value the item lane takes (bagGroupItemLane.mjs EXPECTED_VALUE_MAX, pinned by a test). */
export const BAG_ITEM_EXPECTED_VALUE_MAX = 4000;

/** What Edit item calls each field when it says which one changed. */
const ITEM_FIELD_WORDS: Partial<Record<keyof BagGroupItemStoredFields, string>> = { title: 'name', note: 'notes' };

/**
 * After the lane refused an edit as 'item_changed': which of the fields she
 * changed someone else also changed (their stored value now differs from the
 * one the form opened with), so the form reloads only those and keeps her
 * other edits. Both sides are stored values, already trimmed and null for
 * nothing, so plain equality is the lane's own comparison.
 */
export function bagGroupItemConflictedFields(
  opened: BagGroupItemStoredFields | undefined,
  now: BagGroupItemStoredFields | undefined,
  changedKeys: ReadonlyArray<keyof BagGroupItemStoredFields>,
): Array<keyof BagGroupItemStoredFields> {
  if (!opened || !now) return [...changedKeys];
  return changedKeys.filter((key) => (opened[key] ?? null) !== (now[key] ?? null));
}

/**
 * The line Edit item shows after 'item_changed', naming what someone else
 * changed. `keptOthers` is true when she also changed a field nobody else
 * did, which the form keeps as she typed it.
 */
export function bagGroupItemChangedLine(keys: ReadonlyArray<keyof BagGroupItemStoredFields>, keptOthers = false): string {
  const words = keys.map((key) => ITEM_FIELD_WORDS[key] ?? key);
  if (words.length === 0) {
    return 'Someone else changed this item just now, so your edit was not saved. Take another look and try again.';
  }
  const what = words.length === 1 ? `the ${words[0]}` : `the ${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;
  const kept = keptOthers ? ' Your other changes are still in the form.' : '';
  return `Someone else changed ${what} just now, so your edit was not saved. The form now shows ${what} as saved.${kept} Take a look and save again.`;
}
